import bindAll from "lodash/bindAll";
import includes from "lodash/includes";
import isUndefined from "lodash/isUndefined";

import Backbone from "pages/common/lib/backbone-helpers";
import Persistence from "pages/common/lib/grabyo-persistence";
import Constants from "const/constants";
import { DEFAULT_STUDIO_USER_PROFILE_IMAGE_URL } from "const/images";

const SocialCredential = Backbone.Model.extend({
    idAttribute: `socialId`,

    defaults: {
        enabled: false,
        disabledIfFeatureNotSupported: false,
        tokenStatus: `unknown`,
        profileImage: DEFAULT_STUDIO_USER_PROFILE_IMAGE_URL
    },

    url: function () {
        return `${Constants.DEFAULT_API_PATH}/account/social-credentials/${this.id}`;
    },

    shareUrl: function (shareId) {
        return `/ws/shares/${shareId}/socialmediashare`;
    },

    versionedShareUrl: function (shareId) {
        return `${Constants.DEFAULT_API_PATH}/shares/${shareId}/social-media`;
    },

    parse: function (response) {
        response.type = response.credentialType;
        response.platformId = response.socialId.split(`:`)[1];
        return response;
    },

    initialize: function () {
        bindAll(
            this,
            `onToggleStatusSuccess`,
            `onToggleStatusFail`,
            `onShareSuccessful`,
            `hasGIFSupport`,
            `has360VideoSupport`,
            `isFeatureSupported`,
            `restoreFacebookCrosspostPageIds`
        );

        this.restoreFacebookCrosspostPageIds();
    },

    restoreFacebookCrosspostPageIds: function () {
        const pageIds = Persistence.PermStore.get(
            `facebookCrosspostPageIds:${this.id}`
        );

        if (pageIds?.length) {
            this.set(`facebookCrosspostPageIds`, pageIds);
        }
    },

    isActive: function () {
        return this.get(`status`) === `active`;
    },

    isDeprecated: function () {
        return Constants.DEPRECATED_CREDENTIAL_TYPES.includes(this.get(`type`));
    },

    getDisplayName: function () {
        return this.get(`name`);
    },

    getTypeForSharing: function () {
        if (this.isFacebookPage()) {
            return `facebookPage`;
        } else {
            return this.get(`type`);
        }
    },

    toJSON: function () {
        var json = SocialCredential.__super__.toJSON.apply(this, arguments);
        json.displayName = this.getDisplayName();
        json.isInvalid = this.isInvalid();

        return json;
    },

    toggleStatus: function () {
        return $.ajax({
            type: `PUT`,
            url: `${this.url()}/status`,
            processData: false,
            contentType: `application/json`
        })
            .done((response) => this.onToggleStatusSuccess(response.data))
            .fail(this.onToggleStatusFail);
    },

    isMonetizationEnabled: function () {
        return false;
    },

    getMonetizationShareInfo: function () {
        return {};
    },

    onToggleStatusSuccess: function (data) {
        this.set(data);
    },

    onToggleStatusFail: function () {
        this.trigger(`fail:toggleStatus`, this);
    },

    onDeleteSuccess: function (req, callback) {
        this.collection.remove(this);
        callback && callback(req.responseJSON);
    },

    onDeleteError: function (req, callback) {
        callback && callback(req.responseJSON);
    },

    delete: function ({ onSuccess, onError }) {
        return $.ajax({
            type: `DELETE`,
            url: this.url(),
            dataType: `json`,
            success: (req) => this.onDeleteSuccess(req, onSuccess),
            error: (req) => this.onDeleteError(req, onError)
        });
    },

    isFacebookPage: function () {
        return this.get(`type`) === `facebook`;
    },

    isTwitterAccount: function () {
        return this.get(`type`) === `twitter`;
    },

    isYoutubeChannel: function () {
        return this.get(`type`) === `youtube`;
    },

    isTwitchAccount: function () {
        return this.get(`type`) === `twitch`;
    },

    isInstagramAccount: function () {
        return this.get(`type`) === `instagram`;
    },

    isThePlatformAccount: function () {
        return this.get(`type`) === `theplatform`;
    },

    isFtpAccount: function () {
        return this.get(`type`) === `ftp`;
    },

    isTikTokAccount: function () {
        return this.get(`type`) === `tiktok`;
    },

    isGloboAccount: function () {
        return this.get(`type`) === `globo`;
    },

    isThreadsAccount: function () {
        return this.get(`type`) === `threads`;
    },

    isSpredfastAccount: function () {
        return this.get(`type`) === `spredfast`;
    },

    isInvalid: function () {
        return this.get(`status`) === `invalid`;
    },

    isSocialMedia: function () {
        return false;
    },

    isClipSharingEnabledForType: function () {
        return !includes([`opta`, `twitch`], this.get(`type`));
    },

    isLiveStreamingEnabledForType: function () {
        return includes([`facebook`, `youtube`, `twitch`], this.get(`type`));
    },

    has360VideoSupport: function () {
        return this.isFacebookPage() || this.isYoutubeChannel();
    },

    hasGIFSupport: function () {
        return (
            this.isFacebookPage() ||
            this.isSpredfastAccount() ||
            this.isTwitterAccount() ||
            this.isThePlatformAccount() ||
            this.isInstagramAccount()
        );
    },

    isFeatureSupported: function (feature) {
        switch (feature) {
            case `gif`:
                return this.hasGIFSupport();
            case `360video`:
                return this.has360VideoSupport();
        }
    },

    setDisabledStatus: function (value) {
        this.set(`disabledIfFeatureNotSupported`, value, { silent: true });
        if (value) {
            this.set(`enabled`, false);
        }
    },

    share: function (shareId, data) {
        if (
            this.isTwitterAccount() ||
            this.isFacebookPage() ||
            this.isTikTokAccount() ||
            this.isGloboAccount() ||
            this.isThreadsAccount() ||
            this.isInstagramAccount()
        ) {
            return $.ajax({
                url: this.versionedShareUrl(shareId),
                type: `POST`,
                contentType: `application/json`,
                processData: false,
                data: {
                    ...data,
                    type: this.getTypeForSharing(),
                    userSocialCredentialId: this.id
                }
            }).then(this.onShareSuccessful);
        } else {
            return $.post(this.shareUrl(shareId), {
                userSocialCredentialId: this.id,
                ...data
            }).then(this.onShareSuccessful);
        }
    },

    onShareSuccessful: function (key, response) {
        if (!isUndefined(App.Collections.grabs)) {
            var grab = App.Collections.grabs?.getGrabByShareId?.(
                response.shareId
            );
            if (!isUndefined(grab)) {
                grab.set(`shareCount`, grab.get(`shareCount`) + 1);
                grab.share.set(key, grab.share.get(key) + 1);
            }
        }
    },

    getClipMaxDuration: function () {
        return Infinity;
    },

    getPersistedSettings: function () {
        return Persistence.PermStore.get(`socialCredential:${this.id}`);
    },

    loadPersistedSettings: function () {
        this.set(this.getPersistedSettings());
    }
});

export default SocialCredential;
